import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { ArrowLeft, Calendar, Clock, MapPin, User } from "lucide-react";
import { toast } from "sonner";
import { CATEGORY_BADGE } from "@/lib/categoryColors";
import { formatDateRange, formatTimeRange } from "@/lib/dateFormat";

export default function EventDetailPage() {
  const { id } = useParams();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/events/${id}`)
      .then((r) => setEvent(r.data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return <div className="p-10 text-center text-muted_text">Chargement…</div>;
  if (!event) return (
    <div className="max-w-3xl mx-auto px-4 py-16 text-center">
      <h2 className="font-heading text-2xl text-ink mb-4">Événement introuvable</h2>
      <Link to="/" className="text-sage hover:underline">← Retour à l'accueil</Link>
    </div>
  );

  return (
    <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12 fade-in" data-testid="event-detail">
      <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-muted_text hover:text-sage mb-6" data-testid="back-link">
        <ArrowLeft className="w-4 h-4" /> Retour aux événements
      </Link>

      <div className="rounded-2xl overflow-hidden border border-[#F0E0CC] bg-white">
        <img
          src={event.image_url}
          alt={event.title}
          className="w-full aspect-[16/9] sm:aspect-[21/9] object-cover bg-[#F0E0CC]"
          onError={(e) => { e.currentTarget.src = "https://images.unsplash.com/photo-1517457373958-b7bdd4587205?w=1200&q=80"; }}
        />
        <div className="p-6 sm:p-10">
          <div className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold uppercase tracking-wider mb-4 ${CATEGORY_BADGE[event.category] || "bg-[#FEF3C7] text-sage"}`}>
            {event.category}
          </div>
          <h1 className="font-heading font-bold text-3xl sm:text-4xl lg:text-5xl text-ink leading-tight" data-testid="event-title">
            {event.title}
          </h1>

          <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="flex items-start gap-3 p-4 rounded-xl bg-[#FEFCE8]">
              <Calendar className="w-5 h-5 text-sage mt-0.5" />
              <div>
                <div className="text-xs uppercase tracking-wider text-muted_text font-semibold">
                  {event.end_date && event.end_date !== event.date ? "Dates" : "Date"}
                </div>
                <div className="font-medium text-ink mt-0.5">{formatDateRange(event.date, event.end_date, { full: !event.end_date || event.end_date === event.date })}</div>
              </div>
            </div>
            <div className="flex items-start gap-3 p-4 rounded-xl bg-[#FEFCE8]">
              <Clock className="w-5 h-5 text-sage mt-0.5" />
              <div>
                <div className="text-xs uppercase tracking-wider text-muted_text font-semibold">Horaires</div>
                <div className="font-medium text-ink mt-0.5">{formatTimeRange(event.time, event.end_time)}</div>
              </div>
            </div>
            <div className="flex items-start gap-3 p-4 rounded-xl bg-[#FEFCE8]">
              <MapPin className="w-5 h-5 text-sage mt-0.5" />
              <div>
                <div className="text-xs uppercase tracking-wider text-muted_text font-semibold">Lieu</div>
                <div className="font-medium text-ink mt-0.5">{event.commune}</div>
              </div>
            </div>
          </div>

          <div className="mt-8 prose prose-neutral max-w-none">
            <h3 className="font-heading text-lg font-semibold text-ink mb-2">À propos de l'événement</h3>
            <p className="text-muted_text whitespace-pre-line leading-relaxed">{event.description}</p>
          </div>

          {event.creator_name && (
            <div className="mt-10 pt-6 border-t border-[#F0E0CC] flex items-center gap-2 text-sm text-muted_text">
              <User className="w-4 h-4" /> Publié par <span className="font-medium text-ink">{event.creator_name}</span>
            </div>
          )}
        </div>
      </div>
    </article>
  );
}
